import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
  Visibility as ViewIcon,
  Fullscreen as FullscreenIcon
} from '@mui/icons-material';

const PreviewReportPDFViewer = ({
  open,
  onClose,
  pdfUrl,
  pdfData,
  reportTitle = "Preview Report",
  loading = false,
  error = null,
  disableDownload = false // Add prop to disable download
}) => {
  const [viewerLoading, setViewerLoading] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);

  // Handle PDF download
  const handleDownload = () => {
    if (pdfData) {
      // If we have PDF data as blob
      const blob = new Blob([pdfData], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${reportTitle.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else if (pdfUrl) {
      // If we have PDF URL
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `${reportTitle.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Handle open in new tab
  const handleOpenInNewTab = () => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    } else if (pdfData) {
      const blob = new Blob([pdfData], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Clean up after a delay
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  };

  // Toggle fullscreen
  const handleToggleFullscreen = () => {
    setFullscreen(!fullscreen);
  };

  // Get PDF source for iframe
  const getPdfSource = () => {
    if (pdfUrl) {
      return `${pdfUrl}#toolbar=0&navpanes=0&scrollbar=1&view=FitH`;
    } else if (pdfData) {
      const blob = new Blob([pdfData], { type: 'application/pdf' });
      return URL.createObjectURL(blob) + '#toolbar=0&navpanes=0&scrollbar=1&view=FitH';
    }
    return null;
  };
    const handleRightClick = (e) => {
    e.preventDefault();
  };

  const pdfSource = getPdfSource();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={fullscreen ? false : 'lg'}
      fullWidth
      fullScreen={fullscreen}
      PaperProps={{
        sx: {
          height: fullscreen ? '100vh' : '85vh',
          backgroundColor: '#f5f5f5',
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: 'primary.main',
          color: 'white',
          py: 1,
        }}
      >
        <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
          <ViewIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          {reportTitle}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {pdfSource && (
            <>
              {!disableDownload && (
                <Box sx={{ display: 'none' }}>
                  <Tooltip title="Download PDF">
                    <IconButton
                      onClick={handleDownload}
                      sx={{ color: 'white' }}
                      size="small"
                    >
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Open in New Tab">
                    <IconButton
                      onClick={handleOpenInNewTab}
                      sx={{ color: 'white' }}
                      size="small"
                    >
                      <OpenInNewIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}

              <Tooltip title={fullscreen ? "Exit Fullscreen" : "Fullscreen"}>
                <IconButton
                  onClick={handleToggleFullscreen}
                  sx={{ color: 'white' }}
                  size="small"
                >
                  <FullscreenIcon />
                </IconButton>
              </Tooltip>
            </>
          )}

          <Tooltip title="Close">
            <IconButton
              onClick={onClose}
              sx={{ color: 'white' }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ p: 0, backgroundColor: '#e0e0e0' }}>
        {loading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '400px',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <CircularProgress size={60} />
            <Typography variant="body1" color="text.secondary">
              Loading preview report...
            </Typography>
          </Box>
        )}

        {error && (
          <Box sx={{ p: 3 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          </Box>
        )}

        {!loading && !error && pdfSource && (
          <Box
            sx={{
              position: 'relative',
              height: fullscreen ? 'calc(100vh - 64px)' : '70vh',
            }}
          >
            {viewerLoading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  zIndex: 1,
                }}
              >
                <CircularProgress />
              </Box>
            )}

            <iframe
              src={pdfSource}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              title={reportTitle}
              onLoad={() => setViewerLoading(false)}
               onContextMenu={handleRightClick}
              onError={() => setViewerLoading(false)}
            />
          </Box>
        )}

        {!loading && !error && !pdfSource && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '400px',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Typography variant="h6" color="text.secondary">
              No PDF available to display
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please check if the preview report has been generated.
            </Typography>
          </Box>
        )}
      </DialogContent>

      {!fullscreen && (
        <DialogActions sx={{ backgroundColor: '#f5f5f5', py: 1 }}>
          <Button onClick={onClose} variant="outlined">
            Close
          </Button>
          {pdfSource && (
            <Box sx={{ display: 'none' }}>
              <Button
                onClick={handleDownload}
                variant="contained"
                startIcon={<DownloadIcon />}
              >
                Download
              </Button>
              <Button
                onClick={handleOpenInNewTab}
                variant="contained"
                startIcon={<OpenInNewIcon />}
                sx={{ ml: 1 }}
              >
                Open in New Tab
              </Button>
            </Box>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default PreviewReportPDFViewer;